import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  fetchCommentSentiments,
  fetchCommentThemes,
  fetchCommentMessagingAngles,
  fetchAllComments,
} from "../../apiService";
import {
  Creative,
  CreativeReportData,
  CreativeReportRow,
} from "../../../creatives/types";
import { CreativeSectionConfiguration, CreativeReportMetadata } from "@/types";
import { cn } from "@/lib/utils";
import {
  LuFerrisWheel,
  LuMessageSquarePlus,
  LuMessagesSquare,
  LuSmile,
} from "react-icons/lu";
import { Button } from "@/components/ui/button";
import FacebookIcon from "@/assets/icons/facebook.svg";
import InstagramIcon from "@/assets/icons/instagram.svg";
import { RxOpenInNewWindow } from "react-icons/rx";
import ImageSvg from "@/assets/image.svg";
import VideoSvg from "@/assets/video.svg";
import MixedSvg from "@/assets/mixed.svg";
import { Image } from "lucide-react";
import { capitalizeFirstLetter } from "@/screens/utils/Helpers";
import CreativeGalleryPreview from "@/screens/creatives/components/preview/CreativeGalleryPreview";
import axios from "axios";
import { CancelTokenSource } from "axios";
import NoCommentsMessage from "./NoCommentsMessage";
import TikTokIcon from "@/assets/icons/tiktok.png";
import { throttle } from "lodash";
import { IconCards } from "@tabler/icons-react";
import PageLoader from "@/components/common/loaders/PageLoader";
import { RiArrowUpDownLine, RiArrowDownLine, RiArrowUpLine } from "react-icons/ri";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";
import { SqButton } from "@/components/squircle-ui/button";
import LinkedInIcon from "@/assets/icons/linkedin-logo.png";

interface CommentAnalysisTableProps {
  accountId: string | undefined;
  selectedFilters: CreativeSectionConfiguration | null;
  selectedDate: CreativeReportMetadata | null;
  maxHeight?: string;
  ad_id?: string;
  isLoadingAdList?: boolean;
}

const GROUP_TAB_CONFIG = [
  {
    id: "theme" as const,
    label: "Overall Theme",
    Icon: LuFerrisWheel,
  },
  {
    id: "sentiment" as const,
    label: "Overall Sentiment",
    Icon: LuSmile,
  },
  {
    id: "comments" as const,
    label: "All Comments",
    Icon: LuMessagesSquare,
  },
  {
    id: "messaging" as const,
    label: "New Messaging Angles",
    Icon: LuMessageSquarePlus,
  },
] as const;

const AD_TAB_CONFIG = [
  {
    id: "theme" as const,
    label: "Theme",
    Icon: LuFerrisWheel,
  },
  {
    id: "sentiment" as const,
    label: "Sentiment",
    Icon: LuSmile,
  },
  {
    id: "comments" as const,
    label: "All Comments",
    Icon: LuMessagesSquare,
  },
  {
    id: "messaging" as const,
    label: "New Messaging Angles",
    Icon: LuMessageSquarePlus,
  },
] as const;

const PLATFORM_CONFIG = {
  facebook: {
    icon: FacebookIcon,
    name: "Facebook",
  },
  instagram: {
    icon: InstagramIcon,
    name: "Instagram",
  },
  tiktok: {
    icon: TikTokIcon,
    name: "TikTok",
  },
  linkedin: {
    icon: LinkedInIcon,
    name: "LinkedIn",
  },
} as const;

const mediaSvgs = {
  mixed: MixedSvg,
  video: VideoSvg,
  image: ImageSvg,
  "N/A": MixedSvg,
  "": MixedSvg,
};

const RenderPlatformCell = ({ platform, link }: { platform: string; link?: string }) => {
  const platformConfig =
    PLATFORM_CONFIG[platform.toLowerCase() as keyof typeof PLATFORM_CONFIG];

  return (
    <div className="flex items-center gap-[12px]">
      {platformConfig && (
        <div className="flex items-center gap-[7px]">
          <img
            src={platformConfig.icon}
            alt={platformConfig.name}
            className="w-[15px] h-[15px] object-contain"
          />
          <span className="text-[12.6px] text-[rgba(2,24,89,0.8)] font-[Inter] font-normal leading-[18px]">
            {platformConfig.name}
          </span>
        </div>
      )}
      {link && (
        <button
          onClick={() => window.open(link, "_blank")}
          className="flex w-[32px] h-[32px] justify-center items-center border-none"
        >
          <RxOpenInNewWindow size={16} className="shrink-0 text-brand-primary-800" />
        </button>
      )}
    </div>
  );
};

const RenderAdCell = ({
  mediaItem,
  adName,
  mediaType,
  openGalleryView,
}: {
  mediaItem: Creative | undefined;
  adName: string;
  mediaType: string;
  openGalleryView: () => void;
}) => {
  const [hasImageError, setHasImageError] = useState(false);
  const imageUrl = mediaItem?.images?.[0]?.permalink_url;

  return (
    <div className="w-full max-w-[388px] flex gap-4 items-center">
      <div className="relative group">
        <div className="flex items-center bg-[#EDEFF3] justify-center min-w-16 w-16 h-16 overflow-hidden rounded-[8px]">
          {mediaItem && !hasImageError ? (
            mediaType === "video" ? (
              <img
                src={mediaItem.thumbnail_url}
                alt="Image"
                loading="lazy"
                className="w-full h-full object-cover"
                onError={() => setHasImageError(true)}
              />
            ) : (
              <img
                src={imageUrl}
                alt={"Image"}
                loading="lazy"
                className="w-full h-full object-cover"
                onError={() => setHasImageError(true)}
              />
            )
          ) : (
            <Image size={32} strokeWidth={1.5} className="text-[#B5BCCB]" />
          )}
        </div>
      </div>
      <div className="overflow-hidden flex-1 flex flex-col gap-2">
        <SqTooltipProvider>
          <SqTooltip>
            <SqTooltipTrigger className="w-full">
              <p className="max-w-min whitespace-nowrap truncate text-left text-brand-primary-new font-inter text-[14px] font-normal leading-5">
                {adName}
              </p>
            </SqTooltipTrigger>
            <SqTooltipContent
              side="bottom"
              align="start"
              className="text-[14px] max-w-[340px] text-left z-[60]"
              avoidCollisions
              alignOffset={25}
              collisionPadding={10}
              portal
            >
              <p className="text-[12px] text-left text-white break-all">{adName}</p>
            </SqTooltipContent>
          </SqTooltip>
        </SqTooltipProvider>
        <div className="flex items-center gap-2">
          <div className="flex gap-2 items-center">
            {mediaType && (
              <SqTooltipProvider>
                <SqTooltip>
                  <SqTooltipTrigger>
                    <img
                      src={mediaSvgs[mediaType as keyof typeof mediaSvgs]}
                      alt={mediaType}
                      className="shrink-0 rounded-[8px] h-9 w-9 cursor-default"
                    />
                  </SqTooltipTrigger>
                  <SqTooltipContent side="right" align="start" alignOffset={-10} portal>
                    <p className="text-[12px] text-white">
                      {capitalizeFirstLetter(mediaType)}
                      {mediaType !== "mixed" && "s"}
                    </p>
                  </SqTooltipContent>
                </SqTooltip>
              </SqTooltipProvider>
            )}
          </div>
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              openGalleryView();
            }}
            className="flex justify-center items-center gap-[6px] border border-border-gray bg-white shadow-sm"
          >
            <IconCards size={16} className="shrink-0 text-brand-primary-800" />
            <span className="overflow-hidden text-brand-primary-800 text-ellipsis text-[12px] font-normal leading-[19.157px] tracking-[0.24px]">
              Show Ad
            </span>
          </Button>
        </div>
      </div>
    </div>
  );
};

const RenderToolTippedCell = ({
  text,
  align = "left",
  url,
}: {
  text: React.ReactNode;
  align?: "left" | "right" | "center";
  url?: string;
}) => {
  const content = (
    <div
      className={`text-brand-primary-new text-[12.6px] font-normal leading-[18px] whitespace-normal break-words hover:text-brand-primary-600 line-clamp-3 font-inter ${url ? "cursor-pointer hover:underline" : ""}`}
      style={{ textAlign: align }}
    >
      {text}
    </div>
  );

  return (
    <SqTooltipProvider>
      <SqTooltip>
        <SqTooltipTrigger className="w-full" asChild={!!url}>
          {url ? (
            <a href={url} target="_blank" rel="noopener noreferrer" className="block">
              {content}
            </a>
          ) : (
            content
          )}
        </SqTooltipTrigger>
        <SqTooltipContent
          side="bottom"
          align="start"
          className="text-[12px] max-w-[340px] text-left z-[60]"
          avoidCollisions
          alignOffset={25}
          collisionPadding={10}
          portal
        >
          <p className="whitespace-normal break-words">{text}</p>
        </SqTooltipContent>
      </SqTooltip>
    </SqTooltipProvider>
  );
};

const CommentAnalysisTable: React.FC<CommentAnalysisTableProps> = ({
  accountId,
  selectedFilters,
  selectedDate,
  maxHeight,
  ad_id,
  isLoadingAdList,
}) => {
  const [selectedTab, setSelectedTab] = useState<
    "sentiment" | "theme" | "messaging" | "comments"
  >("theme");
  const [tableData, setTableData] = useState<CreativeReportData | null>(null);
  const [loading, setLoading] = useState(true);
  const [pollingTimeout, setPollingTimeout] = useState<NodeJS.Timeout | null>(null);
  const isMounted = useRef(true);
  const [galleryMediaItems, setGalleryMediaItems] = useState<
    (Creative & {
      type: "image" | "video" | "mixed";
      ad_name: string;
      creative_id: string;
      ad_id: string;
    })[]
  >([]);
  const [openGalleryView, setOpenGalleryView] = useState(false);
  const [cancelTokenSource, setCancelTokenSource] = useState<CancelTokenSource | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [showFade, setShowFade] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [visibleRows, setVisibleRows] = useState<CreativeReportRow[]>([]);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);
  const ROW_HEIGHT = 89;
  const BUFFER_ROWS = 10;

  const handleTabChange = (tab: "sentiment" | "theme" | "messaging" | "comments") => {
    setSelectedTab(tab);
    setVisibleRows([]);
    setStartIndex(0);
    setSortColumn(null);
    setSortDirection(null);
  };

  const handleSort = (columnKey: string) => {
    if (sortColumn === columnKey) {
      // Cycle through: asc -> desc -> no sort
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else if (sortDirection === "desc") {
        setSortColumn(null);
        setSortDirection(null);
      }
    } else {
      // New column, start with asc
      setSortColumn(columnKey);
      setSortDirection("asc");
    }
    // Reset scroll position when sorting changes
    setStartIndex(0);
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  };

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (cancelTokenSource) {
        cancelTokenSource.cancel("Operation cancelled due to component unmount");
      }
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    };
  }, [cancelTokenSource, pollingTimeout]);

  const fetchData = async () => {
    if (!accountId || !selectedFilters || !selectedDate) return;
    setLoading(true);
    setError(null);

    if (cancelTokenSource) {
      cancelTokenSource.cancel("New request initiated");
    }

    const source = axios.CancelToken.source();
    setCancelTokenSource(source);

    try {
      let response;
      const apiParams = {
        accountId: accountId,
        sectionData: selectedFilters,
        reportData: selectedDate,
        cancelToken: source.token,
        ...(ad_id && { ad_id: ad_id }),
      };

      switch (selectedTab) {
        case "sentiment":
          response = await fetchCommentSentiments(apiParams);
          break;
        case "theme":
          response = await fetchCommentThemes(apiParams);
          break;
        case "messaging":
          response = await fetchCommentMessagingAngles(apiParams);
          break;
        case "comments":
          response = await fetchAllComments(apiParams);
          break;
      }

      if (!isMounted.current) return;

      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }

      setTableData(response);

      if (isMounted.current && response?.status === "pending") {
        const timeoutId = setTimeout(() => {
          void fetchData();
        }, 5000);
        setPollingTimeout(timeoutId);
      } else {
        setPollingTimeout(null);
        setLoading(false);
        setCancelTokenSource(null);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error fetching data:", error);
        setError(error instanceof Error ? error.message : "An unexpected error occurred");
        setLoading(false);
        setCancelTokenSource(null);
      }
    }
  };

  useEffect(() => {
    // Cancel any existing request when dependencies change
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Operation cancelled due to dependency change");
      setCancelTokenSource(null);
    }

    // Clear any existing polling timeout
    if (pollingTimeout) {
      clearTimeout(pollingTimeout);
      setPollingTimeout(null);
    }

    if (ad_id && isLoadingAdList) {
      return;
    }

    // Only fetch if we have required data
    if (accountId && selectedFilters && selectedDate) {
      void fetchData();
    }

    // Cleanup function
    return () => {
      if (cancelTokenSource) {
        cancelTokenSource.cancel("Operation cancelled due to cleanup");
      }
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId, selectedTab, selectedFilters, selectedDate, isLoadingAdList]);

  // Sort rows based on sortColumn and sortDirection
  const sortedRows = useMemo(() => {
    if (!tableData?.data?.rows || !sortColumn || !sortDirection) {
      return tableData?.data?.rows || [];
    }

    const rows = [...tableData.data.rows];
    const column = tableData.data.columns.find((col) => col.key === sortColumn);

    return rows.sort((a, b) => {
      let aValue = a[sortColumn as keyof CreativeReportRow];
      let bValue = b[sortColumn as keyof CreativeReportRow];

      // Handle compound columns (e.g., compound_key for URLs)
      if (column?.column_type === "compound" && column.compound_key) {
        aValue = a[column.compound_key as keyof CreativeReportRow];
        bValue = b[column.compound_key as keyof CreativeReportRow];
      }

      // Handle N/A values
      const isNAValue = (val: unknown) => {
        if (val === null || val === undefined) return true;
        if (typeof val === "object") return false;
        if (typeof val === "string" || typeof val === "number") {
          const str = String(val);
          return str === "N/A" || str === "n/a" || str === "";
        }
        return false;
      };

      const aIsNA = isNAValue(aValue);
      const bIsNA = isNAValue(bValue);

      // N/A values always go to the bottom
      if (aIsNA && bIsNA) return 0;
      if (aIsNA) return 1;
      if (bIsNA) return -1;

      // Handle number columns
      if (column?.column_type === "number") {
        const aNum = Number(aValue) || 0;
        const bNum = Number(bValue) || 0;
        return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
      }

      // Handle text columns
      const getStringValue = (val: unknown): string => {
        if (val === null || val === undefined) return "";
        if (typeof val === "string") return val.toLowerCase();
        if (typeof val === "number") return String(val).toLowerCase();
        return "";
      };
      const aStr = getStringValue(aValue);
      const bStr = getStringValue(bValue);

      if (sortDirection === "asc") {
        return aStr.localeCompare(bStr);
      } else {
        return bStr.localeCompare(aStr);
      }
    });
  }, [tableData, sortColumn, sortDirection]);

  const handleScroll = useCallback(() => {
    if (tableContainerRef.current && sortedRows.length > 0) {
      const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
      const isScrollable = scrollHeight > clientHeight + 50;
      const isNearBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 50;
      setShowFade(isScrollable && !isNearBottom);

      const newStartIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - BUFFER_ROWS);
      const visibleCount = Math.ceil(clientHeight / ROW_HEIGHT) + 2 * BUFFER_ROWS;
      setStartIndex(newStartIndex);
      setVisibleRows(
        sortedRows.slice(
          newStartIndex,
          Math.min(newStartIndex + visibleCount, sortedRows.length),
        ),
      );
    }
  }, [sortedRows]);

  const throttledHandleScroll = useMemo(
    () => throttle(handleScroll, 200),
    [handleScroll],
  );

  // Clean up throttled function on unmount
  useEffect(() => {
    return () => {
      throttledHandleScroll.cancel();
    };
  }, [throttledHandleScroll]);

  // Update the initial fade state effect to run after data is loaded
  useEffect(() => {
    setShowFade(false); // Hide fade when loading or no data
  }, [tableData, selectedTab]);

  useEffect(() => {
    if (sortedRows.length > 0) {
      const initialVisibleCount =
        Math.ceil((tableContainerRef.current?.clientHeight || 0) / ROW_HEIGHT) +
        2 * BUFFER_ROWS;
      setVisibleRows(sortedRows.slice(0, initialVisibleCount));
      setStartIndex(0);
    }
  }, [sortedRows]);

  if (!loading && (error || tableData?.status === "failed")) {
    return (
      <div className={`w-full h-full ${ad_id ? "rounded-[12px] border" : ""}`}>
        <div className="my-[250px] w-full flex flex-col justify-center items-center">
          <div className="text-destructive text-lg font-medium mb-2">
            Error loading data
          </div>
          <div className="text-muted-foreground text-sm mb-4">
            Failed to fetch data for this section. Please try again.
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setError(null);
              void fetchData();
            }}
            className="min-w-[80px]"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (!loading && (!tableData?.data?.rows || tableData.data.rows.length === 0)) {
    return (
      <div className={`w-full h-full ${ad_id ? "rounded-[12px] border" : ""}`}>
        <NoCommentsMessage />
      </div>
    );
  }

  const { columns = [] } = tableData?.data || { columns: [] };

  return (
    <div
      className="w-full h-full flex flex-col"
      style={maxHeight ? { maxHeight: maxHeight } : {}}
    >
      <div className="flex rounded-t-[12px] border border-b-0 bg-white">
        {(ad_id ? AD_TAB_CONFIG : GROUP_TAB_CONFIG).map(({ id, label, Icon }) => (
          <button
            key={id}
            className={cn(
              "flex items-center gap-2 px-4 py-2 border-b-2 transition-colors",
              selectedTab === id
                ? "border-b-[1.5px] border-brand-orange text-primary"
                : "border-transparent hover:text-primary",
            )}
            onClick={() => handleTabChange(id)}
          >
            <Icon
              className={`${selectedTab === id ? "text-brand-orange" : "text-brand-primary-800"}`}
              size={16}
            />
            <span
              className={`${selectedTab === id ? "text-brand-orange" : "text-brand-primary-800"} overflow-hidden text-ellipsis text-xs font-normal leading-[19.157px] tracking-[0.24px]`}
            >
              {label}
            </span>
          </button>
        ))}
      </div>

      <div className="relative max-h-full border rounded-b-[12px] overflow-hidden bg-white">
        {loading ? (
          <div className="h-[500px] w-full flex items-center justify-center p-4">
            <PageLoader message={tableData?.message} />
          </div>
        ) : (
          <div
            className="relative max-h-full overflow-auto"
            ref={tableContainerRef}
            onScroll={throttledHandleScroll}
          >
            <Table className="w-full">
              <TableHeader className="sticky top-0 bg-white z-10">
                <TableRow className="after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[1px] after:bg-border">
                  {columns.map((column, index) => {
                    const isSorted = sortColumn === column.key;
                    const currentSort = isSorted ? sortDirection : null;

                    return (
                      <TableHead
                        key={column.key}
                        className={cn(
                          "px-4 py-3 group",
                          column.column_type === "number" ? "text-right" : "text-left",
                        )}
                        style={{
                          borderRight:
                            index === columns.length - 1 ? "none" : "1px solid #E4E4E7",
                          minWidth: column.column_type === "text" ? "420px" : "156px",
                          maxWidth: "420px",
                          fontFamily: "Inter",
                          fontSize: "12.6px",
                          fontStyle: "normal",
                          fontWeight: 500,
                          lineHeight: "18px",
                        }}
                      >
                        <div
                          className={cn(
                            "flex items-center",
                            column.column_type === "number"
                              ? "justify-end gap-3"
                              : "justify-between",
                          )}
                        >
                          <span>{column.label}</span>
                          <SqTooltipProvider delayDuration={100}>
                            <SqTooltip>
                              <SqTooltipTrigger>
                                <SqButton
                                  variant="icon"
                                  size={"sm"}
                                  bgColor="white"
                                  hasOutline
                                  hoverBgColor="#FBFBFB"
                                  className={cn(
                                    "h-7 w-7 shrink-0 transition-opacity",
                                    currentSort
                                      ? "opacity-100"
                                      : "opacity-0 group-hover:opacity-100",
                                  )}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSort(column.key);
                                  }}
                                >
                                  {!currentSort ? (
                                    <RiArrowUpDownLine size={14} color="#9AA3BD" />
                                  ) : currentSort === "asc" ? (
                                    <RiArrowDownLine size={14} color="#9AA3BD" />
                                  ) : (
                                    <RiArrowUpLine size={14} color="#9AA3BD" />
                                  )}
                                </SqButton>
                              </SqTooltipTrigger>
                              <SqTooltipContent align="start" side="bottom" portal>
                                {!currentSort
                                  ? "Sort"
                                  : currentSort === "asc"
                                    ? "Ascending"
                                    : "Descending"}
                              </SqTooltipContent>
                            </SqTooltip>
                          </SqTooltipProvider>
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Top spacer */}
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{
                      height: `${startIndex * ROW_HEIGHT}px`,
                      padding: 0,
                      border: "none",
                    }}
                  />
                </tr>

                {/* Visible rows */}
                {visibleRows.map((row, rowIndex) => (
                  <TableRow key={`row-${startIndex + rowIndex}`}>
                    {(columns || []).map((column, columnIndex) => (
                      <TableCell
                        key={`cell-${startIndex + rowIndex}-${columnIndex}`}
                        className={`px-4 py-3 overflow-hidden ${
                          column.column_type === "number" ? "text-right" : "text-left"
                        }`}
                        style={{
                          borderRight:
                            columnIndex === columns.length - 1
                              ? "none"
                              : "1px solid #E4E4E7",
                          borderBottom:
                            rowIndex === visibleRows.length - 1
                              ? "none"
                              : "1px solid #E4E4E7",
                          color: "rgba(2, 24, 89, 0.80)",
                          fontFamily: "Inter",
                          fontSize: "12.6px",
                          fontStyle: "normal",
                          fontWeight: 400,
                          lineHeight: "18px",
                        }}
                      >
                        {column.key === "platform" ? (
                          <RenderPlatformCell
                            platform={String(row[column.key])}
                            link={
                              row.comment_url
                                ? (row.comment_url as string | undefined)
                                : undefined
                            }
                          />
                        ) : column.column_type === "number" ? (
                          <div className="hover:text-brand-primary-600">
                            {`${column.prefix || ""}${Number(row[column.key] || 0).toLocaleString()}${column.suffix || ""}`}
                          </div>
                        ) : column.key === "ad_name" ? (
                          <RenderAdCell
                            mediaItem={row.ads?.[0]?.creative}
                            adName={String(row[column.key])}
                            mediaType={row.ads?.[0]?.creative_type || "image"}
                            openGalleryView={() => {
                              const creative = row.ads?.[0]?.creative;
                              if (creative) {
                                setGalleryMediaItems([
                                  {
                                    ...creative,
                                    type: row.ads?.[0]?.creative_type || "image",
                                    ad_name: String(row[column.key]),
                                    creative_id: row.ads?.[0]?.creative_id || "",
                                    ad_id: row.ads?.[0]?.ad_id || "",
                                  },
                                ]);
                                setOpenGalleryView(true);
                              }
                            }}
                          />
                        ) : column.key === "comment_text" ? (
                          <RenderToolTippedCell
                            text={String(row[column.key])}
                            align="left"
                            url={row.comment_url as string | undefined}
                          />
                        ) : (
                          <RenderToolTippedCell
                            text={String(row[column.key])}
                            align="left"
                          />
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {/* Bottom spacer */}
                <tr>
                  <td
                    colSpan={columns.length}
                    style={{
                      height: `${Math.max(0, (sortedRows?.length || 0) - (startIndex + visibleRows.length)) * ROW_HEIGHT}px`,
                      padding: 0,
                      border: "none",
                    }}
                  />
                </tr>
              </TableBody>
            </Table>
          </div>
        )}
        {showFade && (
          <div className="sticky bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none" />
        )}
      </div>
      <CreativeGalleryPreview
        open={openGalleryView}
        onOpenChange={setOpenGalleryView}
        creatives={galleryMediaItems}
        accountId={accountId || ""}
        enableSearch={galleryMediaItems.length > 4}
      />
    </div>
  );
};

export default CommentAnalysisTable;
